import React, { useState } from 'react';
import Footer from '../../../components/Footer';
import VegIcon from '../../../components/VegIcon';
import OffersRenderer from '../../../components/OffersRenderer';
import './ClassicElegance.css';

import DEFAULT_LOGO from '../../../assets/defaults/logo-default.jpg';
import DEFAULT_BG from '../../../assets/defaults/bg-default.jpg';
import DEFAULT_ITEM from '../../../assets/defaults/item-default.jpg';

const ClassicElegance = ({ data }) => {
	// Safe Destructuring
	const restaurant = data?.restaurant || {};
	const menus = data?.menus || [];

	const [activeMenuId, setActiveMenuId] = useState(menus[0]?.id);
	const [selectedItem, setSelectedItem] = useState(null);

	const activeMenu = menus.find(m => m.id === activeMenuId) || menus[0];

	if (!data) return <div className="loading">Loading...</div>;

	return (
		<div className="classic-elegance-container">
			{/* Header / Restaurant Info */}
			<header className="restaurant-header" style={{ backgroundImage: `url(${restaurant.backgroundImageUrl || DEFAULT_BG})`, backgroundColor: '#333' }}>
				<div className="header-overlay">
					<div className="restaurant-info">
						<img src={restaurant.logoUrl || DEFAULT_LOGO} alt={restaurant.name} className="restaurant-logo" />
						<h1 title={restaurant.name}>{restaurant.name || 'Restaurant Name'}</h1>
						<p className="description" title={restaurant.description}>{restaurant.description || ''}</p>
						<div className="contact-info">
							{restaurant.phoneNumber && <a href={`tel:${restaurant.phoneNumber}`}>{restaurant.phoneNumber}</a>}
							{restaurant.phoneNumber && restaurant.address && <span className="separator">•</span>}
							{restaurant.address && <span>{restaurant.address}</span>}
						</div>
					</div>
				</div>
			</header>

			{/* Offers / Notes */}
			<OffersRenderer offers={data?.offers} />

			{/* Menu Tabs */}
			{menus.length > 1 && (
				<nav className="menu-tabs">
					{menus.map(menu => (
						<button
							key={menu.id}
							className={`tab-button ${activeMenuId === menu.id ? 'active' : ''}`}
							onClick={() => setActiveMenuId(menu.id)}
						>
							{menu.name}
						</button>
					))}
				</nav>
			)}

			{/* Menu Content */}
			<main className="menu-content">
				{activeMenu ? (
					<div className="menu-section">
						<h2>{activeMenu.name}</h2>
						<p className="menu-description">{activeMenu.description}</p>

						{/* Categories */}
						{activeMenu.categories?.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)).map(category => (
							<div key={category.id} className="category-section">
								<h3 className="category-title">{category.name}</h3>
								<div className="items-grid">
									{category.items?.map(item => (
										<MenuItem key={item.id} item={item} onClick={() => setSelectedItem(item)} />
									))}
								</div>
							</div>
						))}

						{/* Uncategorized Items */}
						{activeMenu.uncategorizedItems?.length > 0 && (
							<div className="category-section">
								<h3 className="category-title">Other Items</h3>
								<div className="items-grid">
									{activeMenu.uncategorizedItems.map(item => (
										<MenuItem key={item.id} item={item} onClick={() => setSelectedItem(item)} />
									))}
								</div>
							</div>
						)}
					</div>
				) : (
					<div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
						No menu details found.
					</div>
				)}
			</main>

			{selectedItem && (
				<div className="ce-modal-overlay" onClick={() => setSelectedItem(null)}>
					<div className="ce-modal" onClick={e => e.stopPropagation()}>
						<button className="ce-modal-close" onClick={() => setSelectedItem(null)}>✕</button>
						<div className="ce-modal-img">
							<img src={selectedItem.imageUrl || DEFAULT_ITEM} alt={selectedItem.name} />
						</div>
						<div className="ce-modal-body">
							<h3>{selectedItem.name}</h3>
							<p className="ce-modal-price">₹{(selectedItem.price || 0).toLocaleString()}</p>
							<p className="ce-modal-desc">{selectedItem.description}</p>

							<div className="ce-modal-variants">
								<h4>Customizations</h4>
								{selectedItem.variants?.filter(v => v.isActive).map(v => (
									<div key={v.id} className="ce-v-row">
										<span>{v.name}</span>
										<span className="ce-v-price">
											{v.price > 0 ? `₹${v.price.toLocaleString()}` : 'Included'}
										</span>
									</div>
								))}
								{(!selectedItem.variants || selectedItem.variants.filter(v => v.isActive).length === 0) && (
									<p className="no-variants">No specific customizations available.</p>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			<Footer />
		</div>
	);
};

const MenuItem = ({ item, onClick }) => {
	if (!item) return null;

	return (
		<div className={`menu-item ${!item.isAvailable ? 'unavailable' : ''}`} onClick={onClick} style={{ cursor: 'pointer' }}>
			<div className="item-image-container">
				<img src={item.imageUrl || DEFAULT_ITEM} alt={item.name} loading="lazy" />
			</div>
			<div className="item-details">
				<div className="item-header-row">
					<div className="ce-name-group">
						{(item.isVeg !== undefined && item.isVeg !== null) && (
							<span className={`ce-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
								<span className="ce-veg-dot"></span>
							</span>
						)}
						<h4 className="item-name" title={item.name}>{item.name}</h4>
					</div>
					<div className="item-price-line"></div>
					<span className="item-price">₹{(item.price || 0).toFixed(2)}</span>
				</div>

				{/* Tags */}
				{item.tags && item.tags.length > 0 && (
					<div className="item-tags">
						{item.tags.map(tag => (
							<span key={tag} className="item-badge">{tag}</span>
						))}
					</div>
				)}

				<p className="item-description" title={item.description}>{item.description}</p>
			</div>
		</div>
	);
};

export default ClassicElegance;
